import React from 'react';
import { toast } from 'sonner';
import { getRoleLabel } from '../../utils/adminRoles';

/**
 * Modal that shows a newly created user's one-time password (shown once).
 */
const OneTimePasswordModal = ({ open, credentials, dark, onClose }) => {
  if (!open || !credentials) return null;

  const password = credentials.temporary_password || '';
  const name =
    credentials.full_name ||
    [credentials.first_name, credentials.last_name].filter(Boolean).join(' ') ||
    'New user';

  const handleCopyAndClose = () => {
    if (password) {
      navigator.clipboard?.writeText(password);
      toast.success('Password copied');
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50">
      <div
        className={`rounded-xl shadow-xl max-w-md w-full p-6 ${
          dark ? 'bg-gray-900 text-gray-100' : 'bg-white text-gray-900'
        }`}
        role="dialog"
        aria-labelledby="otp-modal-title"
      >
        <h2
          id="otp-modal-title"
          className={`text-base font-semibold ${dark ? 'text-gray-50' : 'text-gray-900'}`}
        >
          One-time password
        </h2>
        <p className={`text-sm mt-1 ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
          Save this temporary password now — it is shown only once. The user must change it on first login.
        </p>
        <div
          className={`mt-4 rounded-lg border p-3 space-y-2 text-sm ${
            dark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'
          }`}
        >
          <p>
            <span className={dark ? 'text-gray-400' : 'text-gray-500'}>Name:</span> {name}
          </p>
          <p>
            <span className={dark ? 'text-gray-400' : 'text-gray-500'}>Email:</span>{' '}
            {credentials.email || '—'}
          </p>
          {credentials.role && (
            <p>
              <span className={dark ? 'text-gray-400' : 'text-gray-500'}>Role:</span>{' '}
              {getRoleLabel(credentials.role)}
            </p>
          )}
          <p>
            <span className={dark ? 'text-gray-400' : 'text-gray-500'}>Temporary password:</span>{' '}
            <code
              className={`font-mono font-semibold select-all ${
                dark ? 'text-emerald-300' : 'text-emerald-700'
              }`}
            >
              {password || '—'}
            </code>
          </p>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className={`px-4 py-2 text-sm rounded-lg border ${
              dark
                ? 'border-gray-700 text-gray-300 hover:bg-gray-800'
                : 'border-gray-300 hover:bg-gray-50'
            }`}
          >
            Close
          </button>
          <button
            type="button"
            onClick={handleCopyAndClose}
            className="px-4 py-2 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700"
          >
            Copy password & close
          </button>
        </div>
      </div>
    </div>
  );
};

export default OneTimePasswordModal;
